
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { TableComponent } from "@components/table/table.component";
import { cloneDeep } from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { SchemalaggningDetailsComponent } from "../schemalaggning-details/schemalaggning-details.component";
import { SchemalaggningViewModel } from "../schemalaggning.models";
import { SchemalaggningService } from "../schemalaggning.service";
import { SchemalaggningListTexts } from "./schemalaggning-list.texts";

@Component({
    selector: "rask-schemalaggning-list",
    templateUrl: "./schemalaggning-list.component.html",
    styleUrls: ["./schemalaggning-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        TableComponent
    ]
})
export class SchemalaggningListComponent implements OnInit {

    texts = SchemalaggningListTexts.texts;
    schemalaggningar: SchemalaggningViewModel[];
    schemalaggningarErrors: SchemalaggningViewModel[];
    tableColumns: RaskColumn[];
    tableColumnsError: RaskColumn[];

    private readonly schemalaggningService = inject(SchemalaggningService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);

    constructor() {
        this.schemalaggningService.currentSchemalaggningar$.subscribe(x => {
            this.schemalaggningar = x.map(y => new SchemalaggningViewModel(y));
            this.schemalaggningarErrors = this.schemalaggningar.filter(e => e.senastStatuskod === "Error");
        });
    }

    ngOnInit(): void {
        this.initColumns();
    }

    errorsInQueue(): boolean {
        return this.schemalaggningarErrors.length > 0;
    }

    private initColumns(): void {
        this.tableColumns = [
            {
                editable: false,
                headerName: this.texts.status,
                field: "status",
                minWidth: 100,
                maxWidth: 100,
                filter: true,
                sortable: true,
                type: "span",
                cellClassRules: {
                    "cell-waiting": (params: any) => params.value === "Waiting",
                    "cell-inactive": (params: any) => params.value === "Inactive"
                }
            },
            {
                editable: false,
                headerName: this.texts.namn,
                field: "jobbNamn",
                minWidth: 150,
                filter: true,
                sortable: true,
                onClick: (schemarad: SchemalaggningViewModel) => this.showDetails(schemarad),
                type: "link",
                cellStyle: {
                    "white-space": "nowrap",
                    overflow: "hidden",
                    "text-overflow": "ellipsis"
                }
            },
            {
                editable: false,
                headerName: this.texts.senastSlut,
                field: "senastSlut",
                minWidth: 150,
                maxWidth: 160,
                filter: true,
                sortable: true,
                type: "span"
            },
            {
                editable: false,
                headerName: this.texts.senastvaraktighet,
                field: "senastVaraktighet",
                minWidth: 60,
                maxWidth: 100,
                filter: true,
                sortable: true,
                type: "span"
            },
            {
                editable: false,
                headerName: this.texts.senastResultat,
                field: "senastResultat",
                minWidth: 200,
                maxWidth: 350,
                filter: true,
                sortable: true,
                type: "span",
                tooltipGenerator: (row, value) => row.senastResultat,
                cellStyle: { "white-space": "normal !important" }
            },
            {
                editable: false,
                headerName: this.texts.senasteStatus,
                field: "senastStatuskod",
                minWidth: 60,
                maxWidth: 100,
                filter: true,
                sortable: true,
                type: "span",
                cellClassRules: {
                    "cell-ok": (params: any) => params.value === "Ok",
                    "cell-error": (params: any) => params.value === "Error"
                }
            },
            {
                editable: false,
                field: " ",
                minWidth: 120,
                maxWidth: 120,
                onClick: (schemarad: SchemalaggningViewModel) => this.schemalagg(schemarad),
                filter: false,
                type: "button",
                text: this.texts.schemalagg
            }
        ] as any[] as RaskColumn[];

        this.addColumnsForErrorList(this.tableColumns);
    }

    private addColumnsForErrorList(tableColumns: RaskColumn[]) {
        this.tableColumnsError = cloneDeep(this.tableColumns);

        const errorColumns = [
            {
                editable: false,
                field: "Trigga",
                minWidth: 120,
                maxWidth: 120,
                onClick: (schemarad: SchemalaggningViewModel) => this.runJobbAgain(schemarad.jobbNamn, "ForsokIgen"),
                filter: false,
                type: "button",
                text: this.texts.runjobbagain
            },
            {
                editable: false,
                field: "Ignorera",
                minWidth: 120,
                maxWidth: 120,
                onClick: (schemarad: SchemalaggningViewModel) => this.runJobbAgain(schemarad.jobbNamn, "Ignorerad"),
                filter: false,
                type: "button",
                text: this.texts.setJobbToIgnore
            }
        ] as any[] as RaskColumn[];

        this.tableColumnsError.push(...errorColumns);
    }

    private showDetails(schemarad: SchemalaggningViewModel): void {
        this.raskModalService
            .open(`${this.texts.modalheader} ${schemarad.jobbNamn}`, SchemalaggningDetailsComponent, "xl", true, schemarad.jobbNamn);
    }

    private schemalagg(schemarad: SchemalaggningViewModel): void {
        this.raskModalService
            .confirm(new ModalSettings(this.texts.schemalaggDialogHead, `${this.texts.schemalaggDialogPromt} ${schemarad.jobbNamn}?`))
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const worker = this.busyService.addWork(this.schemalaggningService.schemalagg(schemarad.jobbNamn));
                    worker.subscribe(() => {
                        this.toastService.add(this.texts.schemalaggConfirm);
                    });
                }
            });
    }

    private runJobbAgain(jobbnamn: string, jobbstatus: string): void {
        const jobb = this.checkForValidJobs(jobbnamn);

        if (jobb == null) {
            this.toastService.add(`Kan inte påverka denna typ av jobb => ${jobbnamn}`);
            return;
        }
        const modalSettings = new ModalSettings(this.texts.triggajobbheader, `Vill du ändra statusen på jobbet <b>${jobbnamn}</b> till <b>${jobbstatus}</b>?`);
        modalSettings.contentIsMarkup = true;

        this.raskModalService
            .confirm(modalSettings)
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const worker = this.busyService.addWork(this.schemalaggningService.modifyJobbStatus(jobb, jobbstatus));
                    worker.subscribe((result) => {
                        this.toastService.add(`Ändrar jobb status till ${jobbstatus}`);
                        if (result) {
                            this.toastService.add(`JobbId: ${result.id}, ${result.message}`);
                        }
                    });
                }
            });
    }

    private checkForValidJobs(jobbnamn: string): string {
        switch (jobbnamn) {
            case "ProcessJobbQueueEnkelUtbytesberakning":
                return "EnkelUtbytesberakning";
            case "ProcessJobbQueueNavetAtgardUppdaterad":
                return "NavetAtgardUppdaterad";
            case "UpdateLagersaldoForLagerplats":
                return "UpdateLagersaldoForLagerplats";
            case "ArbetsorderCentrumkoordinatUpdated":
                return "ArbetsorderCentrumkoordinatUpdated";
            default:
                return null;
        }
    }
}
